import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { Chip } from "@mui/material";
import { JSX } from "react";
import { useTranslation } from "react-i18next";

import { GqlObservationStatus } from "~/__generated__";

type Props = {
    status: GqlObservationStatus;
};

const STATUS_META: Record<
    GqlObservationStatus,
    {
        icon: JSX.Element;
        color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
        labelKey: string;
        defaultLabel: string;
    }
> = {
    [GqlObservationStatus.Approved]: {
        icon: <CheckCircleIcon fontSize="small" />,
        color: "success",
        labelKey: "observationStatus.approved",
        defaultLabel: "Approved",
    },
    [GqlObservationStatus.AwaitingApproval]: {
        icon: <HourglassEmptyIcon fontSize="small" />,
        color: "warning",
        labelKey: "observationStatus.awaitingApproval",
        defaultLabel: "Awaiting Approval",
    },
    [GqlObservationStatus.Deleted]: {
        icon: <HourglassEmptyIcon fontSize="small" />,
        color: "error",
        labelKey: "observationStatus.deleted",
        defaultLabel: "Deleted",
    },

    [GqlObservationStatus.Incomplete]: {
        icon: <HourglassEmptyIcon fontSize="small" />,
        color: "secondary",
        labelKey: "observationStatus.incomplete",
        defaultLabel: "Incomplete",
    },
    [GqlObservationStatus.Rejected]: {
        icon: <CancelIcon fontSize="small" />,
        color: "error",
        labelKey: "observationStatus.rejected",
        defaultLabel: "Rejected",
    },
};

export default function ObservationStatus({ status }: Props) {
    const { t } = useTranslation("observation");
    const current = STATUS_META[status];

    return (
        <Chip
            label={
                <span
                    style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "100%",
                    }}
                >
                    {t(current.labelKey, current.defaultLabel)}
                </span>
            }
            icon={current.icon}
            color={current.color}
            size="small"
            sx={{
                maxWidth: "100%",
                flexShrink: 1,
            }}
        />
    );
}
